import MaterialIcon from './ui/MaterialIcon'
import { useHoldStepper } from '../hooks/useHoldStepper'
import { KILO } from '../constants/KILO_THEME'

export type DualZoneCardInputProps = {
  label: string
  value: number
  unit?: string
  icon?: string
  min: number
  max: number
  step?: number
  onChange: (value: number) => void
  className?: string
  formatValue?: (v: number) => string
  /** Omit the outer card shell — renders inner content only, for embedding inside another card. */
  bare?: boolean
}

export function DualZoneCardInput({
  label,
  value,
  unit,
  icon,
  min,
  max,
  step = 1,
  onChange,
  className = '',
  formatValue,
  bare = false,
}: DualZoneCardInputProps) {
  const { activeZone, atLimit, stopHold, bindDualZone, pct } = useHoldStepper({
    value,
    min,
    max,
    step,
    onChange,
  })

  const display = formatValue
    ? formatValue(value)
    : value % 1 === 0
      ? String(value)
      : value.toFixed(1)

  const inner = (
    <>
      <div className="mb-3 flex items-center justify-between">
        <span className={`font-bold ${KILO.fieldLabel}`}>{label}</span>
        {icon ? <MaterialIcon name={icon} size="sm" /> : null}
      </div>

      <div
        className="relative flex h-14 touch-none select-none cursor-pointer items-center rounded-xl bg-kilo-nested/50"
        onPointerDown={bindDualZone}
        onPointerUp={stopHold}
        onPointerLeave={stopHold}
        onPointerCancel={stopHold}
      >
        <div
          className={`pointer-events-none absolute inset-y-0 left-0 w-1/2 rounded-l-xl transition-colors duration-100 ${
            atLimit === 'min'
              ? 'bg-kilo-danger/15'
              : activeZone === 'dec'
                ? 'bg-kilo-accent/10'
                : ''
          }`}
        />
        <div
          className={`pointer-events-none absolute inset-y-0 right-0 w-1/2 rounded-r-xl transition-colors duration-100 ${
            atLimit === 'max'
              ? 'bg-kilo-danger/15'
              : activeZone === 'inc'
                ? 'bg-kilo-accent/10'
                : ''
          }`}
        />

        <span
          className={`pointer-events-none absolute left-4 select-none text-xl font-light transition-colors duration-100 ${
            activeZone === 'dec' ? 'text-kilo-accent' : 'text-kilo-soft'
          }`}
        >
          −
        </span>
        <span
          className={`pointer-events-none absolute right-4 select-none text-xl font-light transition-colors duration-100 ${
            activeZone === 'inc' ? 'text-kilo-accent' : 'text-kilo-soft'
          }`}
        >
          +
        </span>

        <div className="pointer-events-none absolute inset-0 flex items-baseline justify-center gap-1 pt-3.5">
          <span
            className={`text-2xl font-bold leading-none transition-colors duration-100 ${
              atLimit ? 'text-kilo-danger' : KILO.text.primary
            }`}
          >
            {display}
          </span>
          {unit ? (
            <span className={`text-sm leading-none ${KILO.text.muted}`}>{unit}</span>
          ) : null}
        </div>
      </div>

      <div className="mt-3 h-1 overflow-hidden rounded-full bg-kilo-nested">
        <div
          className="h-full rounded-full bg-kilo-accent transition-all duration-150"
          style={{ width: `${pct}%` }}
        />
      </div>
    </>
  )

  if (bare) {
    return <div className={`p-4 ${className}`}>{inner}</div>
  }

  return (
    <div className={`rounded-2xl border border-kilo-border bg-kilo-card p-5 shadow-kilo-card ${className}`}>
      {inner}
    </div>
  )
}

export default DualZoneCardInput
